/**
 * SCENE 01: THE IGNITION OF SILENCE (Stone Age // 35,000 BCE)
 * Ambient slate cavern atmosphere -> touch drag -> dynamic ember particles -> Foley crackle -> cavern illumination.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { MemoryPool } from '../core/MemoryPool';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';
import { globalPRNG } from '../core/PRNG';

export class Scene01_Ignition implements ISceneLifecycle {
  public id = 0;
  public name = 'The Ignition of Silence';
  public era = 'Primordial Vacuum (35,000 BCE)';

  private memoryPool: MemoryPool;
  private eventBus: EventBus;
  private store: StateStore;

  private isIgnited: boolean = false;
  private illuminationIntensity: number = 0.35; // Ambient baseline illumination so page is never pitch blank
  private lightPos: [number, number] = [0, 0];
  private sparkEnergyAccumulator: number = 0;

  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.memoryPool = MemoryPool.getInstance();
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
  }

  public async preload(): Promise<void> {
    return Promise.resolve();
  }

  public mount(): void {}

  public activate(): void {
    // Subscribe to touch drag & strike events
    const unsubStrike = this.eventBus.subscribe(EventType.INPUT_GESTURE_STRIKE, (payload) => {
      this.handleStrike(payload.x || 0, payload.y || 0, payload.velocity || 100);
    });

    const unsubMove = this.eventBus.subscribe(EventType.INPUT_POINTER_MOVE, (payload) => {
      if (payload.velocity && payload.velocity > 10) {
        this.handleMove(payload.x || 0, payload.y || 0, payload.velocity);
      }
    });

    const unsubClick = this.eventBus.subscribe(EventType.INPUT_POINTER_DOWN, (payload) => {
      this.handleStrike(payload.x || 0, payload.y || 0, 150);
    });

    this.unsubscribeList.push(unsubStrike, unsubMove, unsubClick);
  }

  public update(dt: number): void {
    // Decay illumination intensity back towards ambient baseline (0.35)
    if (this.illuminationIntensity > 0.35) {
      this.illuminationIntensity = Math.max(0.35, this.illuminationIntensity - dt * 0.4);
    }

    // Ambient floating embers generator
    if (globalPRNG.nextFloat() < 0.3) {
      const x = globalPRNG.range(-0.8, 0.8);
      const y = globalPRNG.range(-0.8, 0.8);
      const vx = globalPRNG.range(-0.1, 0.1);
      const vy = globalPRNG.range(0.1, 0.4);
      const life = globalPRNG.range(1.0, 2.5);
      const size = globalPRNG.range(1.5, 3.5);
      this.memoryPool.spawnParticle(x, y, 0, vx, vy, 0, life, size, 1.0, 0.6, 0.1, 0.8);
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();

    // Elegant glowing invitation text
    const pulse = (Math.sin(performance.now() * 0.003) * 0.5 + 0.5);
    ctx.font = '600 13px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.letterSpacing = '3px';
    ctx.fillStyle = `rgba(245, 180, 100, ${0.4 + pulse * 0.4})`;
    ctx.shadowColor = 'rgba(226, 91, 45, 0.8)';
    ctx.shadowBlur = 10;
    ctx.fillText('⚡ DRAG OR CLICK ANYWHERE TO IGNITE FLINT', width / 2, height - 50);

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }

  public unmount(): void {}
  public dispose(): void {}

  public getIllumination(): { intensity: number; pos: [number, number] } {
    return {
      intensity: this.illuminationIntensity,
      pos: this.lightPos
    };
  }

  private handleStrike(x: number, y: number, velocity: number): void {
    this.lightPos = [x, y];
    this.illuminationIntensity = Math.min(1.0, this.illuminationIntensity + 0.6);

    if (!this.isIgnited) {
      this.isIgnited = true;
      this.store.setFlag(StateBitmask.TOUCH_IGNITED, true);
      this.eventBus.emit(
        EventType.ACCESSIBILITY_ANNOUNCE,
        0, 0, 0, 0, 0, 1, 0,
        'Flint struck. Primordial embers illuminate ancient slate cavern carving.'
      );
    }

    // Spawn 80 to 250 dynamic ember particles
    const particleCount = Math.floor(Math.min(250, 80 + velocity * 0.3));

    for (let i = 0; i < particleCount; i++) {
      const angle = globalPRNG.range(0, Math.PI * 2);
      const speed = globalPRNG.range(0.5, 3.5);

      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed + globalPRNG.range(0.2, 1.5);
      const vz = globalPRNG.range(-0.5, 0.5);

      const life = globalPRNG.range(0.4, 1.4);
      const size = globalPRNG.range(1.5, 4.0);

      const r = 1.0;
      const g = globalPRNG.range(0.3, 0.85);
      const b = globalPRNG.range(0.05, 0.2);

      this.memoryPool.spawnParticle(x, y, 0, vx, vy, vz, life, size, r, g, b, 1.0);
    }

    // Trigger Web Audio Foley
    this.eventBus.emit(EventType.AUDIO_TRIGGER_SPARK, x, y, 0, 0, velocity, Math.min(1.0, velocity / 300));

    this.sparkEnergyAccumulator += velocity * 0.1;
    this.store.addEnergy(velocity * 0.1);

    if (this.sparkEnergyAccumulator > 600) {
      this.eventBus.emit(EventType.SCENE_CHANGE_REQUEST, 0, 0, 0, 0, 0, 1, 1);
    }
  }

  private handleMove(x: number, y: number, velocity: number): void {
    this.lightPos = [x, y];
    this.illuminationIntensity = Math.min(1.0, this.illuminationIntensity + 0.08);

    const count = Math.floor(globalPRNG.range(2, 6));
    for (let i = 0; i < count; i++) {
      const vx = globalPRNG.range(-0.5, 0.5);
      const vy = globalPRNG.range(0.1, 0.8);
      const life = globalPRNG.range(0.3, 0.8);
      const size = globalPRNG.range(1.0, 2.5);

      this.memoryPool.spawnParticle(x, y, 0, vx, vy, 0, life, size, 1.0, 0.6, 0.1, 0.9);
    }
    if (velocity > 30) {
      this.eventBus.emit(EventType.AUDIO_TRIGGER_SPARK, x, y, 0, 0, velocity, 0.3);
    }
  }
}
